import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getAccountsList } from '../utils/urls';

import { Account } from 'types/accounts';
import { ErrorModal, SortItemType } from 'types/modalTypes';

const pageLength = 100;

function getColumnFromId(id: string) {
  const idsList = [
    'Proteus Account ID',
    'Phone Display',
    'CID',
    'SID',
    'PID',
    'Answer Script',
    'Account Name',
    'Rel Manager',
    'City',
    'State',
    'Time',
    'Status',
    'Service',
    'Call Recording',
    'Routing Group',
    'Branch-out Group',
    'Press Any Key to Answer',
    'Twilio Flex Priority',
    'Route to Proteus Account ID',
  ];

  const index = idsList.findIndex(item => item === id);
  return `${index + 1}`;
}

async function fetchAccounts({
  requestData,
  pageParam,
}: TVariables): Promise<TResult> {
  requestData.append('start', `${pageParam}`);
  const response = await fetch(getAccountsList, {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchAccounts, 500);

type TError = ErrorModal;

export type TResult = {
  Data: Array<Account>;
  Draw: number;
  FilteredRecords: number;
  IsAutoRefreshEnabled: boolean;
  IsSuccess: boolean;
  PageNumber: number;
  StatusCode: number;
  TotalRecords: number;
};

type TVariables = {
  requestData: any;
  pageParam: number;
};

function formatRequestData(
  accountStatus: string,
  keywordSearch: string,
  columnsSearch: Record<string, string>,
  sortedColumn: string,
  sortOrder: string,
): FormData {
  const formData: any = new URLSearchParams();
  formData.append('draw', '1');
  formData.append('columns[0][data]', 'AccountStatusFilter');
  formData.append('columns[0][name]', '');
  formData.append('columns[0][searchable]', 'true');
  formData.append('columns[0][orderable]', 'true');
  formData.append(
    'columns[0][search][value]',
    `${accountStatus?.toLowerCase()}`,
  );
  formData.append('columns[0][search][regex]', 'false');

  formData.append('columns[1][data]', 'Id');
  formData.append('columns[1][name]', '');
  formData.append('columns[1][searchable]', 'true');
  formData.append('columns[1][orderable]', 'true');
  formData.append(
    'columns[1][search][value]',
    columnsSearch && columnsSearch['Proteus Account ID']
      ? columnsSearch['Proteus Account ID']
      : '',
  );

  formData.append('columns[2][data]', 'VDN');
  formData.append('columns[2][name]', '');
  formData.append('columns[2][searchable]', 'true');
  formData.append('columns[2][orderable]', 'true');
  formData.append(
    'columns[2][search][value]',
    columnsSearch && columnsSearch['Phone Display']
      ? columnsSearch['Phone Display']
      : '',
  );
  formData.append('columns[2][search][regex]', 'false');
  formData.append('columns[3][data]', 'CID');
  formData.append('columns[3][name]', '');
  formData.append('columns[3][searchable]', 'true');
  formData.append('columns[3][orderable]', 'true');
  formData.append(
    'columns[3][search][value]',
    columnsSearch && columnsSearch.CID ? columnsSearch.CID : '',
  );
  formData.append('columns[3][search][regex]', 'false');
  formData.append('columns[4][data]', 'SID');
  formData.append('columns[4][name]', '');
  formData.append('columns[4][searchable]', 'true');
  formData.append('columns[4][orderable]', 'true');
  formData.append(
    'columns[4][search][value]',
    columnsSearch && columnsSearch.SID ? columnsSearch.SID : '',
  );
  formData.append('columns[4][search][regex]', 'false');
  formData.append('columns[5][data]', 'PID');
  formData.append('columns[5][name]', '');
  formData.append('columns[5][searchable]', 'true');
  formData.append('columns[5][orderable]', 'true');
  formData.append(
    'columns[5][search][value]',
    columnsSearch && columnsSearch.PID ? columnsSearch.PID : '',
  );
  formData.append('columns[5][search][regex]', 'false');
  formData.append('columns[6][data]', 'AnswerScript');
  formData.append('columns[6][name]', '');
  formData.append('columns[6][searchable]', 'true');
  formData.append('columns[6][orderable]', 'true');
  formData.append(
    'columns[6][search][value]',
    columnsSearch && columnsSearch['Answer Script']
      ? columnsSearch['Answer Script']
      : '',
  );
  formData.append('columns[6][search][regex]', 'false');
  formData.append('columns[7][data]', 'Name');
  formData.append('columns[7][name]', '');
  formData.append('columns[7][searchable]', 'true');
  formData.append('columns[7][orderable]', 'true');
  formData.append(
    'columns[7][search][value]',
    columnsSearch && columnsSearch['Account Name']
      ? columnsSearch['Account Name']
      : '',
  );
  formData.append('columns[7][search][regex]', 'false');
  formData.append('columns[8][data]', 'RelationshipManager');
  formData.append(
    'columns[8][name]',
    'RelationshipManager.FirstName,RelationshipManager.LastName',
  );
  formData.append('columns[8][searchable]', 'true');
  formData.append('columns[8][orderable]', 'true');
  formData.append(
    'columns[8][search][value]',
    columnsSearch && columnsSearch['Rel Manager']
      ? columnsSearch['Rel Manager']
      : '',
  );
  formData.append('columns[8][search][regex]', 'false');
  formData.append('columns[9][data]', 'City');
  formData.append('columns[9][name]', '');
  formData.append('columns[9][searchable]', 'true');
  formData.append('columns[9][orderable]', 'true');
  formData.append(
    'columns[9][search][value]',
    columnsSearch && columnsSearch.City ? columnsSearch.City : '',
  );
  formData.append('columns[9][search][regex]', 'false');
  formData.append('columns[10][data]', 'StateProvince');
  formData.append('columns[10][name]', 'StateProvince.Abbreviation');
  formData.append('columns[10][searchable]', 'true');
  formData.append('columns[10][orderable]', 'true');
  formData.append(
    'columns[10][search][value]',
    columnsSearch && columnsSearch.State ? columnsSearch.State : '',
  );
  formData.append('columns[10][search][regex]', 'false');
  formData.append('columns[11][data]', 'TimeZone');
  formData.append('columns[11][name]', 'TimeZone.Abbreviation');
  formData.append('columns[11][searchable]', 'true');
  formData.append('columns[11][orderable]', 'true');
  formData.append(
    'columns[11][search][value]',
    columnsSearch && columnsSearch.Time ? columnsSearch.Time : '',
  );
  formData.append('columns[11][search][regex]', 'false');
  formData.append('columns[12][data]', 'Status');
  formData.append('columns[12][name]', 'Status.Description');
  formData.append('columns[12][searchable]', 'true');
  formData.append('columns[12][orderable]', 'true');
  formData.append(
    'columns[12][search][value]',
    columnsSearch && columnsSearch.Status ? columnsSearch.Status : '',
  );
  formData.append('columns[12][search][regex]', 'false');
  formData.append('columns[13][data]', 'AccountService');
  formData.append('columns[13][name]', 'AccountService.Description');
  formData.append('columns[13][searchable]', 'true');
  formData.append('columns[13][orderable]', 'true');
  formData.append(
    'columns[13][search][value]',
    columnsSearch && columnsSearch.Service ? columnsSearch.Service : '',
  );
  formData.append('columns[13][search][regex]', 'false');

  formData.append('columns[14][data]', 'EnableRecording');
  formData.append('columns[14][name]', '');
  formData.append('columns[14][searchable]', 'true');
  formData.append('columns[14][orderable]', 'true');
  formData.append(
    'columns[14][search][value]',
    columnsSearch &&
      columnsSearch['Call Recording'] &&
      columnsSearch['Call Recording']?.toLocaleLowerCase() === 'yes'
      ? 'true'
      : columnsSearch['Call Recording']?.toLocaleLowerCase() === 'no'
      ? 'false'
      : '',
  );
  formData.append('columns[14][search][regex]', 'false');

  formData.append('columns[15][data]', 'TwilioAccountWorkFlow');
  formData.append(
    'columns[15][name]',
    'TwilioAccountWorkFlow.TwilioWorkFlow.Name',
  );
  formData.append('columns[15][searchable]', 'true');
  formData.append('columns[15][orderable]', 'true');
  formData.append(
    'columns[15][search][value]',
    columnsSearch && columnsSearch['Routing Group']
      ? columnsSearch['Routing Group']
      : '',
  );
  formData.append('columns[15][search][regex]', 'false');

  formData.append('columns[16][data]', 'TwilioAccountBranchOutCondition');
  formData.append(
    'columns[16][name]',
    'TwilioAccountBranchOutCondition.TwilioBranchOutCondition.Name',
  );
  formData.append('columns[16][searchable]', 'true');
  formData.append('columns[16][orderable]', 'true');
  formData.append(
    'columns[16][search][value]',
    columnsSearch && columnsSearch['Branch-out Group']
      ? columnsSearch['Branch-out Group']
      : '',
  );
  formData.append('columns[16][search][regex]', 'false');
  formData.append('columns[17][data]', 'TwilioPAK');
  formData.append('columns[17][name]', '');
  formData.append('columns[17[searchable]', 'true');
  formData.append('columns[17][orderable]', 'true');
  formData.append(
    'columns[17][search][value]',
    columnsSearch &&
      columnsSearch['Press Any Key to Answer'] &&
      columnsSearch['Press Any Key to Answer']?.toLocaleLowerCase() === 'yes'
      ? 'true'
      : columnsSearch['Press Any Key to Answer']?.toLocaleLowerCase() === 'no'
      ? 'false'
      : '',
  );
  formData.append('columns[17][search][regex]', 'false');

  formData.append('columns[18][data]', 'TwilioFlexPriority');
  formData.append('columns[18][name]', '');
  formData.append('columns[18[searchable]', 'true');
  formData.append('columns[18][orderable]', 'true');
  formData.append(
    'columns[18][search][value]',
    columnsSearch && columnsSearch['Twilio Flex Priority']
      ? columnsSearch['Twilio Flex Priority']
      : '',
  );
  formData.append('columns[18][search][regex]', 'false');
  formData.append('columns[19][data]', 'RouteToAccountId');
  formData.append('columns[19][name]', 'RouteToAccount.Name');
  formData.append('columns[19][searchable]', 'true');
  formData.append('columns[19][orderable]', 'true');
  formData.append(
    'columns[19][search][value]',
    columnsSearch && columnsSearch['Route to Proteus Account ID']
      ? columnsSearch['Route to Proteus Account ID']
      : '',
  );
  formData.append('columns[19][search][regex]', 'false');

  formData.append('order[0][column]', sortedColumn);
  formData.append('order[0][dir]', sortOrder);
  formData.append('search[value]', `${keywordSearch}`);
  formData.append('search[regex]', 'false');
  formData.append('length', `${100}`);
  formData.append('timezoneOffset', '-330');
  return formData;
}

function useGetAccounts(
  accountStatus: string,
  keywordSearch: string,
  columnsSearch: Record<string, string>,
  sortItem: SortItemType,
): UseInfiniteQueryResult<TResult, TError> {
  const requestData = formatRequestData(
    accountStatus,
    keywordSearch,
    columnsSearch,
    getColumnFromId(sortItem.sortKey),
    sortItem.sortOrder,
  );
  return useInfiniteQuery(
    ['accounts_list', accountStatus, keywordSearch, columnsSearch, sortItem],
    ({ pageParam = 0 }) => debouncedFetch({ requestData, pageParam }),
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetAccounts };
